import { partition } from "lodash";
import { Language } from "@/i18n/languages";
import { DCEvent } from "@/events/eventsDomain";
import { InvitationType } from "@/types/Invitation";
import { hasShares, ShareTypeFilter, toShareholderRecipients } from "@/stores/ShareholdersStore";
import {
  ExtendedShareholderDTO,
  isOwnShareholder,
  shareholderDisplayName,
  totalNumberOfShares,
  totalNumberOfSharesOfShareholder,
  totalNumberOfVotes,
  totalNumberOfVotesForShareholder,
} from "@/types/Shareholder";
import { findShareTypeName, ShareType, ShareTypeCode } from "@/common/share";
import { formatBirthDateOrOrganisationNumber } from "@/shareholderRegister/shareholderRegisterDomain";
import { FormatPercentOptions, formatNumber, formatPercent } from "@/utils/numbers";
import { OWNERSHIP_DECIMALS } from "@/shareholderRegister/transactions/ownersPerDate/ownersPerDateDomain";
import { TFunction, TMessage } from "@/i18n/tt";
import { ReactNode } from "react";
import { InfoButton } from "@/components-v2/InfoButton";
import { Icon } from "@/components-v2/Icon";
import { VBox } from "@/components-v2/Box";
import { HoverTooltip } from "@/components-v2/Tooltip";
import { Transaction } from "@/types/Transactions";
import { TransactionType } from "@/types/enums/TransactionType";
import { transactionTypeItems, transactionTypeItemsEmptyState } from "@/types/const/Transactions";

export function withUnidentifiedLast<T extends { isUnidentified?: boolean }>(sorted: T[]): T[] {
  const [unidentified, identified] = partition(sorted, (s) => s.isUnidentified === true);
  return [...identified, ...unidentified];
}

export const shareholderPercentOptions = (
  isOwnershipRegister: boolean,
): FormatPercentOptions | undefined =>
  isOwnershipRegister ? { maximumFractionDigits: OWNERSHIP_DECIMALS } : undefined;

export function showsCapTableLink(access: {
  isOwnershipRegister: boolean;
  isOptionsEnabled: boolean;
  allowCapTableView: boolean;
}): boolean {
  return !access.isOwnershipRegister && access.isOptionsEnabled && access.allowCapTableView;
}

export function transactionTypesForMenu(
  isOwnershipRegister: boolean,
  transactionLog: Transaction[],
): TransactionType[] {
  if (isOwnershipRegister) {
    return transactionLog.length === 0
      ? [TransactionType.OWNERS_PER_DATE]
      : [TransactionType.OWNERS_PER_DATE, TransactionType.COMPANY_STATUS_CHANGE];
  }
  return transactionLog.length === 0 ? transactionTypeItemsEmptyState : transactionTypeItems;
}

export const notifyShareholdersEvents = (
  shareholders: ExtendedShareholderDTO[],
  orgNo: string,
  companyName: string | null,
  lang: Language,
  isShareholderPortalEnabled: boolean,
): DCEvent[] => [
  {
    type: "showSendInvitationDialog",
    data: {
      invitationType: InvitationType.SHAREHOLDER,
      organisationNumber: orgNo,
      companyName,
      lang,
      title: { key: "shareholders.notify.all.heading" },
      description: isShareholderPortalEnabled
        ? { key: "shareholders.notify.description" }
        : undefined,
      isShareholderPortalEnabled,
      recipients: toShareholderRecipients(shareholders),
    },
  },
];

export class EmptyShareholderStateEvents {
  onImportShareholders: () => void;
  onImportFromFile: () => void;
  onAddShareholdersManually: () => void;
  onAddShareholdersFromPortfolio: () => void;

  constructor(
    onImportShareholders: () => void,
    onImportFromFile: () => void,
    onAddShareholdersManually: () => void,
    onAddShareholdersFromPortfolio: () => void,
  ) {
    this.onImportShareholders = onImportShareholders;
    this.onImportFromFile = onImportFromFile;
    this.onAddShareholdersManually = onAddShareholdersManually;
    this.onAddShareholdersFromPortfolio = onAddShareholdersFromPortfolio;
  }
}

export function prepTableColumns(
  locale: Language,
  shareTypeFilter: ShareTypeFilter,
  sortedShareholders: ExtendedShareholderDTO[],
  shareSumBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  },
  calculatedVotesBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  },
  filteredTypes: ShareType[],
  isOwnershipRegister: boolean,
  t: TFunction,
): ShareholderColumn[] {
  const percentOptions = shareholderPercentOptions(isOwnershipRegister);
  const sharesColumns =
    shareTypeFilter === "overview"
      ? prepOverviewColumns(
          locale,
          shareSumBySharetypeCode,
          calculatedVotesBySharetypeCode,
          percentOptions,
        )
      : shareTypeFilter === "votes"
        ? prepVotesColumns(locale, filteredTypes, calculatedVotesBySharetypeCode, percentOptions)
        : prepSharesColumns(locale, filteredTypes, shareSumBySharetypeCode, percentOptions);

  return sortedShareholders.length > 0
    ? [
        {
          shareTypeName: "icon",
          label: { translated: "" },
          align: "left",
          width: 10,
          getValue: (s: ExtendedShareholderDTO) => (
            <HoverTooltip
              content={t(
                s.isUnidentified
                  ? "shareholders.unidentified"
                  : hasShares(s)
                    ? s.isCompany
                      ? "shareholders.company-shareholder"
                      : "shareholders.personal-shareholder"
                    : "shareholders.no-shares",
              )}
            >
              {({ targetRef }) => (
                <VBox ref={targetRef}>
                  <Icon
                    name={
                      s.isUnidentified ? "users-three" : s.isCompany ? "buildings" : "user-square"
                    }
                    className={hasShares(s) ? undefined : "opacity-20"}
                  />
                </VBox>
              )}
            </HoverTooltip>
          ),
        },
        {
          name: "name",
          label: { key: "shareholders.name" },
          align: "left",
          getValue: (s) => shareholderDisplayName(s, t),
        },
        {
          name: "birthDateOrOrganisationNumber",
          label: { key: "shareholders.birthDateOrOrganisationNumber" },
          align: "left",
          getValue: (s) => formatBirthDateOrOrganisationNumber(locale, s),
        },
        ...sharesColumns,
      ]
    : [];
}

function prepVotesColumns(
  lang: Language,
  filteredTypes: ShareType[],
  calculatedVotesBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  },
  percentOptions?: FormatPercentOptions,
): ShareholderColumn[] {
  const totalVotesInCompany = totalNumberOfVotes(calculatedVotesBySharetypeCode);
  return [
    {
      shareTypeName: "totalVotes",
      label: { key: "shareholders.total-votes" },
      align: "right",
      width: 60,
      getValue: (s: ExtendedShareholderDTO) =>
        isOwnShareholder(s) ? (
          <InfoButton infoText={{ key: "transactions.own-shares-info" }} />
        ) : (
          formatNumber(lang, totalNumberOfVotesForShareholder(s))
        ),
    },
    {
      shareTypeName: "percentageVotes",
      label: { key: "shareholders.total-votes-percent" },
      align: "right",
      width: 60,
      getValue: (s: ExtendedShareholderDTO) =>
        formatPercent(
          lang,
          totalNumberOfVotesForShareholder(s) / totalVotesInCompany || 0,
          percentOptions,
        ),
    },
    ...filteredTypes.flatMap<ShareholderColumn>((shareType) => [
      {
        shareTypeName: String(shareType.sharetypeId),
        label: {
          key: "shareholders.votes",
          params: {
            shareTypeName: findShareTypeName(shareType, lang),
          },
        },
        align: "right",
        width: 60,
        getValue: (s) =>
          isOwnShareholder(s)
            ? null
            : formatNumber(lang, s.calculatedVotes[shareType.shareTypeCode]),
      },
      {
        shareTypeName: `${shareType.sharetypeId}-percentage`,
        label: {
          key: "shareholders.percent-votes",
          params: {
            shareTypeName: findShareTypeName(shareType, lang),
          },
        },
        align: "right",
        width: 60,
        getValue: (s) =>
          isOwnShareholder(s)
            ? null
            : formatPercent(
                lang,
                s.calculatedVotes[shareType.shareTypeCode]! /
                  calculatedVotesBySharetypeCode[shareType.shareTypeCode]! || 0,
                percentOptions,
              ),
      },
    ]),
  ];
}

function prepOverviewColumns(
  lang: Language,
  shareSumBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  },
  calculatedVotesBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  },
  percentOptions?: FormatPercentOptions,
): ShareholderColumn[] {
  const totalSharesInCompany = totalNumberOfShares(shareSumBySharetypeCode);
  const totalVotesInCompany = totalNumberOfVotes(calculatedVotesBySharetypeCode);
  return [
    {
      shareTypeName: "totalshares",
      label: { key: "transactions.number-of-shares" },
      align: "right",
      width: 60,
      getValue: (s: ExtendedShareholderDTO) =>
        formatNumber(lang, totalNumberOfSharesOfShareholder(s)),
    },
    {
      shareTypeName: "percentageShares",
      label: { key: "ownership.table.ownership" },
      align: "right",
      width: 60,
      getValue: (s) =>
        formatPercent(
          lang,
          totalNumberOfSharesOfShareholder(s) / totalSharesInCompany || 0,
          percentOptions,
        ),
    },
    {
      shareTypeName: "totalVotes",
      label: { key: "transactions.number-of-votes" },
      align: "right",
      width: 60,
      getValue: (s: ExtendedShareholderDTO) =>
        isOwnShareholder(s) ? (
          <InfoButton infoText={{ key: "transactions.own-shares-info" }} />
        ) : (
          formatNumber(lang, totalNumberOfVotesForShareholder(s))
        ),
    },
    {
      shareTypeName: "percentageVotes",
      label: { key: "transactions.percentage-of-votes" },
      align: "right",
      width: 60,
      getValue: (s) =>
        formatPercent(
          lang,
          totalNumberOfVotesForShareholder(s) / totalVotesInCompany || 0,
          percentOptions,
        ),
    },
  ];
}

function prepSharesColumns(
  lang: Language,
  filteredTypes: ShareType[],
  shareSumBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  },
  percentOptions?: FormatPercentOptions,
) {
  return filteredTypes.flatMap<ShareholderColumn>((shareType) => [
    {
      shareTypeName: String(shareType.sharetypeId),
      label: {
        key: "shareholders.shares",
        params: { shareTypeName: findShareTypeName(shareType, lang) },
      },
      align: "right",
      width: 60,
      getValue: (s) => formatNumber(lang, s.calculatedShares[shareType.shareTypeCode]),
    },
    {
      shareTypeName: `${shareType.sharetypeId}-percentage`,
      label: {
        key: "shareholders.percent-shares",
        params: {
          shareTypeName: findShareTypeName(shareType, lang),
        },
      },
      align: "right",
      width: 60,
      getValue: (s) =>
        formatPercent(
          lang,
          s.calculatedShares[shareType.shareTypeCode]! /
            shareSumBySharetypeCode[shareType.shareTypeCode]! || 0,
          percentOptions,
        ),
    },
  ]);
}

interface ShareholderColumn {
  name?: keyof ExtendedShareholderDTO;
  shareTypeName?: string;
  label: TMessage;
  align?: "left" | "right";
  getValue: (s: ExtendedShareholderDTO) => ReactNode;
  notSorted?: boolean;
  width?: number;
}
